/**
 * Shared notification-prefs UI used by both the admin Profile page and
 * the portal Settings page. All logic is self-contained — pass no props.
 *
 * Exposes two named components:
 *   • <NotificationPrefsCard />  — per-report-kind cadence picker
 *   • <MutedSitesCard />         — portal-only per-site mute toggle
 *
 * Both hit the same backend endpoints already exposed at
 *   GET/PATCH /api/users/me/preferences
 *   GET/PUT  /api/portal/muted-sites
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, AlertOctagon, FileSignature, Route, Wrench, Clock, MapPin, ShieldCheck } from "lucide-react";
import api, { errorMessage } from "../lib/api";
import { ActionButton } from "./Primitives";

// Notification event catalogue — order matters. Icons picked from lucide.
const NOTIF_EVENTS = [
  { key: "incident",        label: "Incident Reports",     hint: "Real-time by default. Every incident, every site.",           Icon: AlertOctagon,  defaultRt: true },
  { key: "maintenance",     label: "Maintenance Requests", hint: "Real-time by default. Every new maintenance ticket.",         Icon: Wrench,        defaultRt: true },
  { key: "dar",             label: "Daily Activity (DAR)", hint: "End-of-shift DAR reports.",                                   Icon: FileSignature, defaultRt: false },
  { key: "tour",            label: "Tour Tracks",          hint: "Officer patrol / checkpoint completion.",                       Icon: Route,         defaultRt: false },
  { key: "tardy",           label: "Tardies",              hint: "Late clock-ins vs scheduled shift.",                          Icon: Clock,         defaultRt: false },
  { key: "geofence_breach", label: "Geofence Breach",      hint: "Clocked-in guard leaves the site perimeter.",                 Icon: MapPin,        defaultRt: false },
  { key: "cert_expiry",     label: "Certificate Expiries", hint: "Cert about to expire (30 / 15 / 7-day thresholds).",          Icon: ShieldCheck,   defaultRt: false },
];

const FREQ_OPTIONS = [
  { value: "realtime", label: "Real-time" },
  { value: "daily",    label: "Daily digest (8 AM)" },
  { value: "weekly",   label: "Weekly digest (Mon 8 AM)" },
  { value: "monthly",  label: "Monthly (1st, 8 AM)" },
  { value: "off",      label: "Off" },
];

/**
 * Optional `eventFilter` narrows the displayed rows — useful for the
 * portal settings page which only surfaces the four report kinds a
 * client actually cares about (incident / maintenance / dar / tour).
 */
export function NotificationPrefsCard({ eventFilter }) {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/users/me/preferences")
      .then((r) => setPrefs(r.data || {}))
      .catch((e) => toast.error(errorMessage(e, "Failed to load preferences")));
  }, []);

  const setFreq = (eventKey, freq) => {
    setPrefs((p) => ({
      ...p,
      notification_preferences: { ...(p?.notification_preferences || {}), [eventKey]: freq },
    }));
  };

  const save = async () => {
    if (!prefs) return;
    setSaving(true);
    try {
      await api.patch("/users/me/preferences", {
        notification_preferences: prefs.notification_preferences || {},
      });
      toast.success("Preferences saved");
    } catch (e) { toast.error(errorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const notif = prefs?.notification_preferences || {};
  const events = eventFilter
    ? NOTIF_EVENTS.filter((e) => eventFilter.includes(e.key))
    : NOTIF_EVENTS;

  return (
    <div className="border border-[#27272A] bg-[#121212]" data-testid="notif-prefs-card">
      <div className="px-5 py-4 border-b border-[#27272A] flex items-center gap-2">
        <Bell className="size-4 text-[#FF3B30]" />
        <div>
          <div className="label-mute">Notifications</div>
          <div className="font-display uppercase font-bold text-lg">Delivery preferences</div>
        </div>
      </div>
      <div className="divide-y divide-[#27272A]">
        {events.map(({ key, label, hint, Icon, defaultRt }) => {
          const current = notif[key] || (defaultRt ? "realtime" : "off");
          return (
            <div key={key} className="px-5 py-4 flex items-start gap-4" data-testid={`notif-row-${key}`}>
              <Icon className="size-4 mt-0.5 text-zinc-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{label}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{hint}</div>
              </div>
              <select
                data-testid={`notif-freq-${key}`}
                value={current}
                onChange={(e) => setFreq(key, e.target.value)}
                className="bg-[#0A0A0A] border border-[#27272A] text-xs uppercase tracking-widest px-2 py-1.5 focus:outline-none focus:border-[#FF3B30] min-w-[180px]"
              >
                {FREQ_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          );
        })}
      </div>
      <div className="px-5 py-3 border-t border-[#27272A] flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500">
        <div>Digest emails send at 8 AM in your account&apos;s tz. Real-time = one email per event.</div>
        <ActionButton data-testid="notif-save" onClick={save} disabled={saving || !prefs}>
          {saving ? "Saving…" : "Save preferences"}
        </ActionButton>
      </div>
    </div>
  );
}

/**
 * Portal-only per-site mute toggles. Returns null (renders nothing) for
 * users without addressable sites — safe to drop into any layout.
 */
export function MutedSitesCard() {
  const [data, setData] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/portal/muted-sites")
      .then((r) => setData(r.data || { muted_site_ids: [], sites: [] }))
      .catch(() => setData({ muted_site_ids: [], sites: [] }));
  }, []);

  if (!data) return null;
  if (!data.sites?.length) return null;

  const mutedSet = new Set(data.muted_site_ids || []);
  const toggle = (siteId) => {
    const next = new Set(mutedSet);
    if (next.has(siteId)) next.delete(siteId); else next.add(siteId);
    setData({ ...data, muted_site_ids: Array.from(next) });
    setDirty(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/portal/muted-sites", { muted_site_ids: data.muted_site_ids });
      setData({ ...data, muted_site_ids: r.data.muted_site_ids });
      setDirty(false);
      toast.success("Muted sites saved");
    } catch (e) { toast.error(errorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-[#27272A] bg-[#121212]" data-testid="muted-sites-card">
      <div className="px-5 py-4 border-b border-[#27272A] flex items-center gap-2">
        <Bell className="size-4 text-zinc-500" />
        <div>
          <div className="label-mute">Portal notifications</div>
          <div className="font-display uppercase font-bold text-lg">Mute per site</div>
        </div>
      </div>
      <div className="px-5 py-3 text-xs text-zinc-400 border-b border-[#27272A]">
        Toggle off any site whose report emails you&apos;d rather not receive. Only affects your inbox — other portal users at the same site still get notified.
      </div>
      <div className="divide-y divide-[#27272A] max-h-80 overflow-y-auto">
        {data.sites.map((s) => {
          const isMuted = mutedSet.has(s.id);
          return (
            <label
              key={s.id}
              className="flex items-center justify-between px-5 py-3 cursor-pointer select-none hover:bg-[#1c1c1e]"
              data-testid={`muted-site-row-${s.id}`}
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-zinc-200 truncate">{s.name}</div>
                {s.client_name && <div className="text-[10px] text-zinc-500 font-mono">{s.client_name}</div>}
              </div>
              <input
                type="checkbox"
                data-testid={`muted-site-toggle-${s.id}`}
                className="accent-[#FF3B30]"
                checked={!isMuted}
                onChange={() => toggle(s.id)}
                title={isMuted ? "Currently muted — click to re-enable" : "Click to mute this site"}
              />
            </label>
          );
        })}
      </div>
      <div className="px-5 py-3 border-t border-[#27272A] flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500">
        <div>{mutedSet.size} of {data.sites.length} muted</div>
        <ActionButton
          data-testid="muted-sites-save"
          onClick={save}
          disabled={saving || !dirty}
        >
          {saving ? "Saving…" : dirty ? "Save changes" : "No changes"}
        </ActionButton>
      </div>
    </div>
  );
}
